// ─────────────────────────────────────────────
//  TestDayPanel — pick a facility, a day, and the person you want to be.
//
//  The pre-entry screen for testing mode, and the ONLY moment both databases
//  are reachable: `config.js` swaps `db` to the local plane at module scope, so
//  once a session has entered, the app cannot read live Firestore and cannot
//  snapshot anything. Everything this panel does — the roster read, the capture
//  — has to happen here, before the reload.
//
//  ⚠️ THE ROSTER IS THE DAY'S, NOT TODAY'S DIRECTORY. The existing view-as
//  picker lists `listFacilityStaff(activeFacilityId)` — who is on staff NOW, at
//  the admin's OWN campus. For a past day that answers the wrong question
//  twice: people join and leave, and the point of this screen is to stand in a
//  particular day's shoes.
//
//  ⚠️ IT REFUSES BEFORE IT CAPTURES. A day with nobody impersonable, or a
//  snapshot that could not read rooms, is reported here rather than after a
//  reload — a tester dropped into an empty hospital reasonably concludes the
//  day was quiet.
//
//  Rendered by SetupPage's Testing tab, which is already admin-only.
// ─────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useFacilities } from "../../hooks/useFacilities";
import { facilityLabel } from "../../domain/facilities";
import { dayKey, facilityTz } from "../../domain/dayKey";
import { hasTestableRoster } from "../../domain/testRoster";
import { missingCollections } from "../../domain/testSnapshot";
import { readDayRoster, enterTestMode } from "./enterTestMode";
import tokens from "../../theme/tokens";

const { color, space, radius, font } = tokens;

const ROLE_LABEL = { aic: "AIC", attending: "MD", provider: "CRNA / AA" };

export default function TestDayPanel() {
  // realIsAdmin, never isAdmin: while previewing someone else the effective
  // role is theirs, and gating on it would hide the way back in.
  const { realIsAdmin, realUser } = useAuth();
  const toast = useToast();
  const facilities = useFacilities();

  const [facilityId, setFacilityId] = useState(realUser?.activeFacilityId || "default");
  const facility = useMemo(
    () => (facilities || []).find((f) => f.id === facilityId) || null,
    [facilities, facilityId]
  );

  // ⚠️ THE FACILITY'S DAY, NOT THE DEVICE'S. Two day-key definitions coexist in
  // this codebase; `rosterDateKey` is the device's calendar date and would
  // default this picker to a different day than the board uses.
  const today = dayKey(new Date(), facilityTz(facility));
  const [date, setDate] = useState(today);

  const [roster, setRoster] = useState(null); // null = not looked yet
  const [loading, setLoading] = useState(false);
  const [entering, setEntering] = useState("");
  const [problem, setProblem] = useState("");

  if (!realIsAdmin) return null;

  const loadDay = async () => {
    setLoading(true);
    setProblem("");
    setRoster(null);
    try {
      const people = await readDayRoster(facilityId, date);
      setRoster(people);
      if (!hasTestableRoster(people)) {
        // Named, not silent: an empty picker with no sentence reads as broken.
        setProblem(
          "Nobody on that day has an account this app can sign in as. " +
          "Rows naming a person with no login cannot be impersonated."
        );
      }
    } catch (err) {
      console.error("[testing] could not read that day's roster:", err);
      setProblem("Could not read that day. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const start = async (person) => {
    setEntering(person.uid);
    setProblem("");
    try {
      const res = await enterTestMode({
        facilityId,
        facilityName: facilityLabel(facilityId, facilities),
        date,
        viewAsUid: person.uid,
        viewAsName: person.name,
        rosterUids: (roster || []).map((p) => p.uid),
        // Capture the admin's OWN account too — see the note in enterTestMode.
        // Without it AuthContext silently provisions them a least-privilege
        // profile inside the session and they lose admin rights.
        viewAsRole: person.role,
        enteringUid: realUser?.uid || null,
      });
      if (!res.ok) {
        // Say WHICH collections failed. "Could not enter" alone gives the
        // tester nothing to act on.
        const missing = missingCollections(res.manifest).join(", ");
        setProblem(
          `That day could not be captured completely, so testing mode was not started. ` +
          `Missing: ${missing || "unknown"}.`
        );
        toast.error("Could not start testing mode.");
      }
      // On success enterTestMode reloads; nothing after this runs.
    } catch (err) {
      console.error("[testing] enter failed:", err);
      setProblem(String(err?.message || err));
      toast.error("Could not start testing mode.");
    } finally {
      setEntering("");
    }
  };

  return (
    <div style={st.panel} data-testid="test-day-panel">
      <div style={st.title}>Test a facility on a date</div>
      <p style={st.hint}>
        Loads that day into a local copy and signs you in as somebody who was
        working it. Everything you change stays on this device and is thrown
        away when you leave.
      </p>

      {/* Stated before entering, not discovered afterwards. */}
      <p style={st.warn}>
        ⚠️ Permission rules do <strong>not</strong> run on the local copy, so an
        action the real app would refuse can succeed here. This proves the
        screens and the flow — never the permissions.
      </p>

      <div style={st.row}>
        <label style={st.label} htmlFor="tm-facility">Facility</label>
        <select
          id="tm-facility"
          style={st.input}
          value={facilityId}
          onChange={(e) => { setFacilityId(e.target.value); setRoster(null); }}
        >
          {(facilities || []).map((f) => (
            <option key={f.id} value={f.id}>{f.name || f.label || f.id}</option>
          ))}
        </select>
      </div>

      <div style={st.row}>
        <label style={st.label} htmlFor="tm-date">Date</label>
        <input
          id="tm-date"
          type="date"
          style={st.input}
          value={date}
          // Past and today only for now — a future day has a schedule but no
          // board, and the owner has not ruled on whether that is useful.
          max={today}
          onChange={(e) => { setDate(e.target.value); setRoster(null); }}
        />
      </div>

      <button type="button" style={st.loadBtn} onClick={loadDay} disabled={loading}>
        {loading ? "Reading that day…" : "Load the day"}
      </button>

      {problem && (
        <p style={st.problem} role="alert" data-testid="test-day-problem">{problem}</p>
      )}

      {/* Three states that must not look alike: not looked yet, looked and
          found nobody (the sentence above), and looked and found people. */}
      {roster && roster.length > 0 && (
        <div style={st.people} data-testid="test-day-roster">
          <div style={st.peopleHead}>
            {roster.length} on the board that day — pick who to be
          </div>
          {roster.map((p) => (
            <button
              key={p.uid}
              type="button"
              style={st.person}
              disabled={!!entering}
              onClick={() => start(p)}
            >
              <span style={st.personName}>{p.name || p.uid}</span>
              <span style={st.personRole}>
                {entering === p.uid ? "Starting…" : ROLE_LABEL[p.role] || p.role}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const st = {
  panel: {
    marginTop: space.md,
    padding: space.md,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.md,
    background: color.surface.card,
  },
  title: { fontSize: 14, fontWeight: font.weight.bold, color: color.text.primary },
  hint: { margin: `${space.xs}px 0 0 0`, fontSize: 12, color: color.text.secondary, lineHeight: 1.45 },
  warn: {
    margin: `${space.sm}px 0 0 0`,
    padding: "8px 10px",
    borderLeft: `4px solid ${color.accent.closing}`,
    borderRadius: radius.sm,
    background: "#fef8ec",
    color: "#7a4f00",
    fontSize: 12,
    lineHeight: 1.45,
  },
  row: { display: "flex", alignItems: "center", gap: space.sm, marginTop: space.sm },
  label: { flex: "0 0 72px", fontSize: 12, fontWeight: font.weight.bold, color: color.text.secondary },
  // minWidth 0 so a long facility name shrinks the control instead of pushing
  // the row wider than the panel.
  //
  // ⚠️ 16px, NOT 13. iOS zooms the whole page when a focusable field is under
  // 16px, and this panel is reachable at phone width. The repo guards it —
  // pages/mobile/inputZoom.test.js caught exactly this while it was still 13.
  input: {
    flex: 1, minWidth: 0, minHeight: 36, padding: "6px 8px",
    border: `1px solid ${color.border.default}`, borderRadius: radius.sm,
    background: color.surface.card, color: color.text.primary,
    fontSize: 16, fontFamily: "inherit",
  },
  loadBtn: {
    marginTop: space.sm, minHeight: 36, padding: `0 ${space.md}px`,
    border: `1px solid ${color.border.default}`, borderRadius: radius.sm,
    background: color.surface.card, color: color.text.primary,
    fontSize: 13, fontWeight: font.weight.bold, fontFamily: "inherit", cursor: "pointer",
  },
  problem: {
    margin: `${space.sm}px 0 0 0`, fontSize: 12, lineHeight: 1.45,
    color: "#7f1d1d",
  },
  people: { marginTop: space.sm },
  peopleHead: {
    fontSize: 11, fontWeight: font.weight.bold, letterSpacing: ".3px",
    color: color.text.muted, marginBottom: space.xs,
  },
  person: {
    display: "flex", alignItems: "center", gap: space.sm, width: "100%",
    minHeight: 40, marginTop: 4, padding: "6px 10px",
    border: `1px solid ${color.border.default}`, borderRadius: radius.sm,
    background: color.surface.card, color: color.text.primary,
    textAlign: "left", fontFamily: "inherit", cursor: "pointer",
  },
  personName: { flex: 1, minWidth: 0, fontSize: 13, fontWeight: font.weight.bold },
  personRole: { flexShrink: 0, fontSize: 11, color: color.text.muted },
};
